import React from 'react'

import Image from '../../../../../components/pdf/Image'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { Paragraph, SectionHeading } from './_shared/Prose'

const RESPONSAVEIS_TECNICOS = [
  {
    nome: 'Adilson dos Santos Elidio',
    cargo: 'Técnico de Segurança do Trabalho',
    registro: 'MTB 51/12631-7',
    assinatura: '/pdfs/bradesco/assinatura-adilson-elidio.png'
  },
  {
    nome: 'Joyce Dhaiane de Almeida Paiva',
    cargo: 'Engenheira de Segurança do Trabalho',
    registro: 'CREA MG 127488 / CREA RJ 2021101943',
    assinatura: '/pdfs/bradesco/assinatura-joyce-paiva.png'
  }
]

/**
 * Seção 20 - Considerações Finais. Texto literal do modelo aprovado por Julia Buzzo
 * ("Modelo PGR 2026 - Banco Bradesco_Varejo"). Substitui o placeholder institucional
 * anterior (arquivo era `20.DisposicoesFinaisSection.js` — o título real desta seção é
 * "Considerações Finais", não "Disposições Finais").
 *
 * O bloco de responsáveis técnicos reproduz as assinaturas manuscritas extraídas da
 * página 55 do modelo (`assinatura-adilson-elidio.png` / `assinatura-joyce-paiva.png` em
 * `public/pdfs/bradesco/`), recortadas para conter apenas o traço da assinatura — nome,
 * cargo e registro continuam sendo texto real (não bitmap), como no restante do PGR
 * gerado dinamicamente. Nome do primeiro responsável corrigido para "Adilson dos Santos
 * Elidio" (antes "Wilson dos Santos Elídio") — conferido contra a legenda da assinatura
 * na página 55 do modelo.
 *
 * Cor do título "RESPONSÁVEIS TÉCNICOS": preta no modelo real (RGB 0,0,0, confirmado por
 * amostragem de pixel na página 55) — diferente do azul petróleo (`HEADING_COLOR`) usado
 * nos títulos numerados de seção/subseção em todo o resto do documento.
 *
 * Bloco "RESPONSÁVEIS TÉCNICOS" (título + assinaturas) envolvido num único `<View noWrap>`
 * (=> CSS `page-break-inside: avoid`, ver `components/pdf/View.js`) para que ele nunca seja
 * partido entre duas páginas — ex.: assinatura numa página e nome/cargo/registro na
 * seguinte. Como nenhuma seção do corpo do documento força quebra de página antes de si
 * (ver histórico em `PdfDocument.js`), o bloco continua fluindo imediatamente depois do
 * texto corrido desta seção; só vai para a página seguinte, como unidade única, se
 * realmente não houver espaço restante na página atual — nunca isolado sozinho por conta
 * de ter sido dividido no meio. As imagens de assinatura já usavam `maxWidth`/`maxHeight`
 * fixos + `ignoreFixedSize` (proporção preservada, sem distorção, e imunes ao controle
 * "altura das imagens" do formulário de opções do PDF, que só afeta fotos de atividade)
 * desde o PR anterior que as introduziu — nenhuma mudança necessária aí.
 */
const ConsideracoesFinaisSection = () => (
  <View id="20">
    <Separator weight={15} />
    <SectionHeading numero={20}>CONSIDERAÇÕES FINAIS</SectionHeading>

    <Paragraph>
      Este Programa de Gerenciamento de Riscos (PGR) foi elaborado com base nos princípios estabelecidos pela Norma
      Regulamentadora nº 1 (NR-1), atualizada pela Portaria MTE nº 1.419 de 27 de agosto de 2024, com vigência a
      partir de 26 de maio de 2025. Atende, de forma integrada, às exigências legais vigentes e visa garantir a
      saúde, a segurança e o bem-estar de todos os funcionários do Banco Bradesco.
    </Paragraph>

    <Paragraph>
      O documento contempla a identificação e avaliação dos perigos e riscos ocupacionais físicos, químicos,
      biológicos, ergonômicos e psicossociais, considerando a severidade das possíveis consequências e a
      probabilidade de ocorrência, conforme os critérios técnicos exigidos na norma.
    </Paragraph>

    <Paragraph>
      Destaca-se que os fatores psicossociais relacionados ao trabalho foram diagnosticados sob o escopo da
      Ergonomia, utilizando a metodologia ERGOCHECK – PREMAPA, alinhada às diretrizes da ISO 45003, garantindo a
      rastreabilidade, auditabilidade e a conformidade do processo com os padrões internacionais e nacionais.
    </Paragraph>

    <Paragraph>
      O PGR deve ser considerado um documento dinâmico e estratégico. Portanto, recomenda-se que seja constantemente
      monitorado, revisado e atualizado com base em novos dados, alterações nos processos, introdução de novas
      tecnologias, mudanças organizacionais e feedback contínuo dos trabalhadores, conforme preconiza a própria NR-1
      em seu ciclo de melhoria contínua.
    </Paragraph>

    <Paragraph>
      Reforçamos a importância da participação ativa das lideranças e dos trabalhadores na execução das ações
      previstas, bem como no fortalecimento da cultura de prevenção e promoção da saúde e segurança no ambiente de
      trabalho.
    </Paragraph>

    <Paragraph>
      Por fim, este PGR reafirma o compromisso institucional do Banco Bradesco com a integridade física e mental de
      seus funcionários, promovendo ambientes seguros, saudáveis e produtivos em conformidade com a legislação
      brasileira e as boas práticas de gestão ocupacional.
    </Paragraph>

    <View noWrap>
      <Separator weight={10} />
      <Text bold size="small" color="#000000">
        RESPONSÁVEIS TÉCNICOS
      </Text>
      <Separator weight={8} />

      <View style={{ display: 'flex', flexDirection: 'row', gap: '40px' }}>
        {RESPONSAVEIS_TECNICOS.map(responsavel => (
          <View key={responsavel.nome} style={{ flex: 1 }}>
            <Image
              src={responsavel.assinatura}
              type="sign"
              maxWidth={200}
              maxHeight={90}
              ignoreFixedSize
              alt={`Assinatura de ${responsavel.nome}`}
            />
            <Text size="p" bold>
              {responsavel.nome}
            </Text>
            <Text size="small">{responsavel.cargo}</Text>
            <Text size="small">{responsavel.registro}</Text>
          </View>
        ))}
      </View>
    </View>

    <Separator weight={15} />
  </View>
)

export default ConsideracoesFinaisSection
